export const PERIODS = [
  { key: 'growth_1m', label: '1M' },
  { key: 'growth_2m', label: '2M' },
  { key: 'growth_5m', label: '5M' },
  { key: 'growth_11m', label: '11M' },
];

export const FUNDAMENTAL_METRICS = [
  { key: 'enterprise_value', label: 'EV', step: 100 },
  { key: 'ebitda', label: 'EBITDA', step: 1 },
  { key: 'ev_to_ebitda', label: 'EV/EBITDA', step: 1 },
];

export const GROWTH_COMPARATORS = [
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
];

const STOP_LOSS_MODES = new Set(['trailing', 'fixed_drop']);
const DEFAULT_STOP_LOSS = { mode: 'trailing', value: 0.05 };

export const FUND_FILTER_MODES = [
  { value: 'off', label: 'Off' },
  { value: 'has', label: 'W/ Data' },
  { value: 'missing', label: 'W/O Data' },
  { value: 'gt', label: 'Greater than' },
  { value: 'lt', label: 'Less than' },
];

export const WATCHLIST_STATE_VERSION = 2;

const ALLOWED_FUND_MODES = new Set(FUND_FILTER_MODES.map((mode) => mode.value));
const ALLOWED_GROWTH_COMPARATORS = new Set(GROWTH_COMPARATORS.map((mode) => mode.value));

const DEFAULT_GROWTH_VALUES = {
  growth_1m: '10',
  growth_2m: '20',
  growth_5m: '50',
  growth_11m: '100',
};

const DEFAULT_GROWTH_FILTER = (value) => ({
  comparator: 'gte',
  value: value ?? '',
  sell: { enabled: true },
});

export function createDefaultFilters() {
  return PERIODS.reduce((acc, { key }) => {
    acc[key] = DEFAULT_GROWTH_FILTER(DEFAULT_GROWTH_VALUES[key] ?? '');
    return acc;
  }, {});
}

export function normalizeGrowthFilters(input) {
  const defaults = createDefaultFilters();
  const source = input && typeof input === 'object' ? input : {};
  return Object.keys(defaults).reduce((acc, key) => {
    const fallback = defaults[key];
    const incoming = source[key];
    if (incoming && typeof incoming === 'object' && !Array.isArray(incoming)) {
      const comparator = ALLOWED_GROWTH_COMPARATORS.has(incoming.comparator)
        ? incoming.comparator
        : fallback.comparator;
      const value = incoming.value !== undefined && incoming.value !== null
        ? String(incoming.value)
        : fallback.value;
      const sellEnabled = Boolean(incoming.sell && typeof incoming.sell === 'object'
        ? incoming.sell.enabled !== false
        : fallback.sell.enabled);
      acc[key] = {
        comparator,
        value,
        sell: { enabled: sellEnabled },
      };
    } else if (incoming !== undefined && incoming !== null && incoming !== '') {
      acc[key] = {
        comparator: fallback.comparator,
        value: String(incoming),
        sell: { ...fallback.sell },
      };
    } else {
      acc[key] = {
        comparator: fallback.comparator,
        value: fallback.value,
        sell: { ...fallback.sell },
      };
    }
    return acc;
  }, {});
}

export function createDefaultFundFilters() {
  return FUNDAMENTAL_METRICS.reduce((acc, { key }) => {
    acc[key] = { mode: 'off', value: '' };
    return acc;
  }, {});
}

function sanitizeMetaList(value) {
  if (value === null) return null;
  if (!Array.isArray(value)) return null;
  const seen = new Set();
  const cleaned = [];
  value.forEach((entry) => {
    const normalized = typeof entry === 'string' ? entry.trim() : String(entry || '');
    if (!normalized) return;
    const lower = normalized.toLowerCase();
    if (seen.has(lower)) return;
    seen.add(lower);
    cleaned.push(normalized);
  });
  return cleaned.length ? cleaned : null;
}

export function sanitizeGrowthState(raw) {
  const defaultFund = createDefaultFundFilters();
  const baseline = {
    filters: normalizeGrowthFilters(raw?.filters),
    fundFilters: { ...defaultFund },
    sortState: { key: 'symbol', direction: 'asc' },
    pinnedSymbols: [],
    restrictPinned: false,
    metaFilters: {
      country: null,
      symbol_type: null,
      industries: null,
    },
    filtersCollapsed: false,
    stopLoss: { ...DEFAULT_STOP_LOSS },
  };

  if (!raw || typeof raw !== 'object') {
    return baseline;
  }

  const fundSource = raw.fundFilters && typeof raw.fundFilters === 'object' ? raw.fundFilters : {};
  Object.keys(defaultFund).forEach((key) => {
    const altKey = key.replace(/_([a-z])/g, (_, chr) => chr.toUpperCase());
    const entry = fundSource[key] ?? fundSource[altKey];
    if (entry && typeof entry === 'object') {
      const mode = ALLOWED_FUND_MODES.has(entry.mode) ? entry.mode : 'off';
      baseline.fundFilters[key] = {
        mode,
        value:
          mode === 'gt' || mode === 'lt'
            ? entry.value !== undefined && entry.value !== null
              ? String(entry.value)
              : ''
            : '',
      };
    } else {
      baseline.fundFilters[key] = { ...defaultFund[key] };
    }
  });

  if (raw.sortState && typeof raw.sortState === 'object' && raw.sortState.key) {
    const direction = raw.sortState.direction === 'desc' ? 'desc' : 'asc';
    baseline.sortState = { key: raw.sortState.key, direction };
  }

  if (Array.isArray(raw.pinnedSymbols)) {
    baseline.pinnedSymbols = raw.pinnedSymbols
      .filter((symbol) => typeof symbol === 'string')
      .map((symbol) => symbol.trim().toUpperCase())
      .filter(Boolean);
  }
  baseline.restrictPinned = Boolean(raw?.restrictPinned);

  const meta = raw.metaFilters && typeof raw.metaFilters === 'object' ? raw.metaFilters : {};
  baseline.metaFilters = {
    country: sanitizeMetaList(meta.country),
    symbol_type: sanitizeMetaList(meta.symbol_type),
    industries: sanitizeMetaList(meta.industries),
  };

  if (raw.filtersCollapsed !== undefined) {
    baseline.filtersCollapsed = Boolean(raw.filtersCollapsed);
  }

  const stopLoss = raw.stopLoss && typeof raw.stopLoss === 'object' ? raw.stopLoss : {};
  const mode = STOP_LOSS_MODES.has(stopLoss.mode) ? stopLoss.mode : 'trailing';
  const value = mode === 'fixed_drop' ? Number(stopLoss.value) || 0.05 : 0.05;
  baseline.stopLoss = { mode, value };

  return baseline;
}
